/**
 * 通用「可直接键入 + 拼音模糊搜索」下拉选择器（**Web 端**）。
 *
 * 依据：**B 域 PRD §5.8**（尤其 §5.8.3 行为约定 · §5.8.4 噪音规避）·
 * `AC-B7.1`~`AC-B7.12` · §5.8.6 双端口径（**Web 支持 / 小程序不做**）。
 * 移植自 v1 `components/SearchSelect.tsx`（PRD §5.8：「直接移植，不重写」）。
 *
 * ## 🔴 三条刻意的实现决定
 *
 * 1. **默认过滤不在本组件实现** —— 一律走 `lib/pinyin-match.ts` 的 `searchByName`。
 *    §5.8.5 明确：本能力与 C3「交易搜索」**不得合并实现、也不得互相替代**。
 * 2. **`matchText` 与 `label` 分离**（§5.8.4 噪音规避）—— 账户显示「招商银行（尾号 1234）」，
 *    但参与匹配的文本只取「招商银行 1234」。否则「**尾号**」二字进入拼音首字母串后，
 *    任何含 `wh` 的输入都会命中该账户，制造噪音候选。
 * 3. **空态必须显式**（§5.8.3「无匹配」行）—— `noOptionsText` 默认「无匹配项」，
 *    **禁止静默显示空列表**。
 *
 * ## 🆕 批 `115` 的两处扩展（`DR-066` §5）
 *
 * | 扩展 | 给谁用 | 为什么放在**这里**而不是组件外 |
 * |---|---|---|
 * | `optionFilter` | 分类的两段式下钻（空输入只列一级 + 已展开的二级） | MUI 的过滤发生在**它自己内部**，组件外只能拿到过滤后的结果 ⇒ 口径必须能从外面注进来 |
 * | `renderTrailing` | 分类行尾的 `▸` 展开按钮 | 账户 / 其它选择器不需要它；**不把分类逻辑写进通用组件** |
 *
 * 🔴 `optionFilter` **替换**了批 `110` 的 `idleSubset`：后者的语义是"空输入只列这些 id"，
 * 而分类现在需要的是"空输入列一级 + 按展开态插二级" —— 那是**同一个位置上的两种口径**，
 * 留两套会立刻分叉（一处按子集、一处按展开态，谁生效取决于传了哪个参数）。
 * 旧断言（"空输入列子集"）已按 `AC-B5.8` 改写成等价场景，见 `docs/changelog/2026-10-28-b115-frontend-polish.md`。
 *
 * ## §5.8.3 行为约定的落地位置
 *
 * | 约定 | 落地 |
 * |---|---|
 * | 空输入返回全部且**保持传入顺序** | `searchByName` 空串时 `return [...items]` |
 * | 排序稳定 | `searchByName` 里 `order` 兜底 |
 * | 无匹配给明确空态 | 本组件 `noOptionsText` |
 * | 面板宽度自适应、**不折行** | `slotProps.paper` + `renderOption` 的 `nowrap` |
 * | 点击即展开 / 聚焦全选 / 首项高亮 / 失焦不清空 | `openOnFocus` / `selectOnFocus` / `autoHighlight` / `clearOnBlur={false}` |
 */

import { Autocomplete, Box, TextField } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { searchByName } from '../lib/pinyin-match.js';

/** 可搜索选择器的最小选项结构。 */
export interface SearchSelectOption {
  /** 稳定标识；**空串代表「未选择」** */
  id: string;
  /** 展示文案，**同时作为默认的匹配文本** */
  label: string;
  /**
   * **参与匹配**的文本；默认取 `label`。
   *
   * 🔴 用于把「给人看的文案」与「参与搜索的文本」分开（§5.8.4）：
   * 账户显示「招商银行（尾号1234）」但匹配串只取「招商银行 1234」，
   * 避免「尾号」二字进入拼音首字母串后制造噪音候选。
   */
  matchText?: string;
  /** 层级缩进（分类树用：L1 = 0，L2 = 1）。**不参与匹配**，只影响呈现。 */
  indent?: 0 | 1;
}

/**
 * 候选过滤口径 —— `(全部候选, 输入文本) => 要显示的候选`。
 *
 * 🔴 必须是**纯函数**：MUI 会在每次键入时调它，且调用时机不受本组件控制。
 */
export type OptionFilter<T extends SearchSelectOption> = (options: readonly T[], query: string) => T[];

export interface SearchSelectProps<T extends SearchSelectOption> {
  readonly options: readonly T[];
  /** 当前选中项 id；**空串表示未选中** */
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly label?: string;
  readonly placeholder?: string;
  readonly size?: 'small' | 'medium';
  readonly fullWidth?: boolean;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: ReactNode;
  readonly sx?: SxProps<Theme>;
  /** 无匹配时的文案（§5.8.3：**必须显式**，禁止静默空列表） */
  readonly noOptionsText?: ReactNode;
  readonly autoFocus?: boolean;
  /**
   * 分组键：传入后下拉按此键分组（如分类按 L1 分组，呈现树状层级）。
   * 传入时 MUI 会自动渲染 group header；与拼音搜索、过滤**完全兼容**。
   */
  readonly groupBy?: (option: T) => string;
  /** 自定义分组标题渲染（如分类的分级标题弱化样式） */
  readonly renderGroup?: (params: { key: number | string; group: string; children?: ReactNode }) => ReactNode;
  /**
   * 🆕 批 `115`：**自定义候选口径**（`DR-066` §5 · `AC-B5.8`）。
   *
   * 不传 = 默认 `searchByName`（空输入原样返回、键入按拼音六级权重搜）。
   * 分类传它来实现"空输入只列一级 + 已展开的二级"。
   */
  readonly optionFilter?: OptionFilter<T>;
  /**
   * 🆕 批 `115`：**行尾附加内容**（分类的 `▸` 展开按钮）。
   *
   * 🔴 只负责渲染，**不负责阻止冒泡** —— 但调用方**必须**同时写：
   * `onMouseDown={e => e.preventDefault()}`（否则 Autocomplete 先失焦关弹层，按钮点不到）
   * 与 `onClick={e => e.stopPropagation()}`（否则同时触发选项选中）。
   * 两者只写一个都不行（批 `115` 实测）。
   */
  readonly renderTrailing?: (option: T) => ReactNode;
}

/**
 * 默认候选过滤 —— 直接转发到共享拼音内核（§5.8.5：与 C3 交易搜索不合并实现）。
 *
 * 🔴 导出是为了让"空输入 / 键入 / 空白串"三条口径能**直接对函数断言**：
 * `renderToStaticMarkup` 基座里 MUI 弹层不挂载，绕道 DOM 只能拿到空列表（假信号）。
 */
export function defaultOptionFilter<T extends SearchSelectOption>(
  opts: readonly T[],
  query: string,
): T[] {
  return searchByName(opts, (o) => o.matchText ?? o.label, query);
}

/**
 * 通用「可直接键入 + 拼音模糊搜索」选择器（web）。
 *
 * 与 MUI `Select` 的区别：输入框可以打字，候选随输入实时过滤，
 * 支持原文子串、拼音首字母（`zs` → 招商银行）、全拼（`yinhang` → 银行）三类匹配，
 * 排序规则见 `lib/pinyin-match.ts`（原文前缀 > 原文子串 > 首字母前缀 > …）。
 */
export function SearchSelect<T extends SearchSelectOption>({
  options,
  value,
  onChange,
  label,
  placeholder,
  size = 'small',
  fullWidth = true,
  disabled = false,
  error,
  helperText,
  sx,
  noOptionsText = '无匹配项',
  autoFocus,
  groupBy,
  renderGroup,
  optionFilter,
  renderTrailing,
}: SearchSelectProps<T>) {
  const selected = options.find((o) => o.id === value) ?? null;
  const filter = optionFilter ?? defaultOptionFilter;

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      sx={sx}
      options={options as T[]}
      value={selected}
      onChange={(_e, next) => onChange(next === null ? '' : next.id)}
      getOptionLabel={(o: T) => o.label}
      isOptionEqualToValue={(o: T, v: T) => o.id === v.id}
      // 🔴 过滤**只**走注入的口径（默认 = 共享拼音匹配内核，§5.8.5）
      filterOptions={(opts, state) => filter(opts, state.inputValue)}
      noOptionsText={noOptionsText}
      autoHighlight
      selectOnFocus
      openOnFocus
      handleHomeEndKeys
      clearOnBlur={false}
      autoFocus={autoFocus}
      groupBy={groupBy}
      renderGroup={renderGroup}
      // 下拉面板宽度自适应内容（`minWidth 100%` 保证至少与输入框等宽），
      // 避免长账户名 / 分类名在窄面板里被折行（§5.8.3「下拉面板」行）。
      slotProps={{
        paper: { sx: { width: 'auto', minWidth: '100%', maxHeight: 340 } },
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        // 🔴 分类树的**缩进**（L1 顶格 / L2 缩进）—— 只影响视觉，不改匹配
        const pad = option.indent === 1 ? 26 : 0;
        const trailing = renderTrailing?.(option);
        return (
          <li key={key} {...optionProps} style={{ whiteSpace: 'nowrap' }}>
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
            >
              <Box component="span" sx={{ pl: `${String(pad)}px`, flex: 1, minWidth: 0 }}>
                {option.label}
              </Box>
              {trailing == null ? null : <Box component="span" sx={{ flexShrink: 0 }}>{trailing}</Box>}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}

export default SearchSelect;
